import { Injectable, signal } from '@angular/core';

export type NotificationType = 'success' | 'error' | 'info' | 'warning';

export interface AppNotification {
  id: number;
  type: NotificationType;
  title: string;
  message: string;
  /** ms hasta auto-cierre; 0 = no se cierra solo. */
  duration: number;
}

/**
 * Notificador central (toasts). Cualquier componente, servicio o interceptor
 * puede emitir avisos de éxito/error/info. La UI la pinta <app-toast-host>.
 *
 *   private notify = inject(NotificationService);
 *   this.notify.success('Empresa registrada');
 *   this.notify.error('No se pudo guardar', 'Error');
 */
@Injectable({ providedIn: 'root' })
export class NotificationService {
  private seq = 0;
  readonly notifications = signal<AppNotification[]>([]);

  success(message: string, title = 'Listo', duration = 4000): number {
    return this.push('success', title, message, duration);
  }

  error(message: string, title = 'Error', duration = 6000): number {
    return this.push('error', title, message, duration);
  }

  info(message: string, title = 'Información', duration = 4000): number {
    return this.push('info', title, message, duration);
  }

  warning(message: string, title = 'Atención', duration = 5000): number {
    return this.push('warning', title, message, duration);
  }

  dismiss(id: number): void {
    this.notifications.update(list => list.filter(n => n.id !== id));
  }

  clear(): void {
    this.notifications.set([]);
  }

  private push(type: NotificationType, title: string, message: string, duration: number): number {
    const id = ++this.seq;
    const notification: AppNotification = { id, type, title, message, duration };
    this.notifications.update(list => [...list, notification]);

    if (duration > 0) {
      setTimeout(() => this.dismiss(id), duration);
    }
    return id;
  }
}
